import { Patient } from '@xolv-mybrightlink/common';
import React, { useEffect, useReducer } from 'react';
import { CareEpisode } from '../model/CareEpisode';
import { isEmpty } from 'lodash';
import { ProgressPortrait } from '../model/ProgressPortrait';
import {
  fetchCareEpisodes,
  fetchProgressPortraitMetrics,
} from '../services/ProgressPortraitService';
import { ProgressPortraitStore } from './ProgressPortraitStore';

export type ProgressPortraitAction =
  | SelectTreatmentPeriod
  | SelectPatient
  | FetchingCareEpisode
  | FetchedCareEpisode
  | FetchingProgressPortraitData
  | FetchedProgressPortraitData;

type SelectPatient = {
  type: 'SELECT_PATIENT';
  patientSfId: string;
};

type FetchingCareEpisode = {
  type: 'FETCHING_CARE_EPISODE';
  patientSfId: string;
};

type SelectTreatmentPeriod = {
  type: 'SELECT_TREATMENT_PERIOD';
  patientSfId: string;
  abaCaseSfId: string;
};

type FetchedCareEpisode = {
  type: 'FETCHED_CARE_EPISODE';
  careEpisodes: CareEpisode[];
  patientSfId: string;
  errorMessage: string;
};

type FetchingProgressPortraitData = {
  type: 'FETCHING_PROGRESS_PORTRAIT_DATA';
  patientSfId: string;
  abaCaseId: string;
};

type FetchedProgressPortraitData = {
  type: 'FETCHED_PROGRESS_PORTRAIT_DATA';
  patientSfId: string;
  abaCaseId: string;
  progressPortraitData: ProgressPortrait | null;
  errorMessage: '';
};

export const useProgressPortrait = (patients: Patient[]) => {
  const [store, dispatch] = useReducer(
    reducer,
    ProgressPortraitStore.init(patients),
  );
  useFetchCareEpisode(store, dispatch);
  useFetchProgressPortraitData(store, dispatch);
  return { store, dispatch };
};

const reducer = (
  state: ProgressPortraitStore,
  action: ProgressPortraitAction,
): ProgressPortraitStore => {
  if (action.type === 'SELECT_PATIENT') {
    return state.newState(action.patientSfId);
  }
  if (action.type === 'FETCHING_CARE_EPISODE') {
    state.fetchingCareEpisode(action.patientSfId);
  }
  if (action.type === 'FETCHED_CARE_EPISODE') {
    if (isEmpty(action.errorMessage)) {
      state.fetchedCareEpisode(action.patientSfId, action.careEpisodes);
    } else {
      state.errorFetchCareEpisode(action.patientSfId, action.errorMessage);
    }
  }
  if (action.type === 'SELECT_TREATMENT_PERIOD') {
    state.selectTreatmentPeriod(action.patientSfId, action.abaCaseSfId);
  }
  if (action.type === 'FETCHING_PROGRESS_PORTRAIT_DATA') {
    state.loadingProgressPortrait(action.patientSfId, action.abaCaseId);
  }
  if (action.type === 'FETCHED_PROGRESS_PORTRAIT_DATA') {
    if (action.progressPortraitData) {
      state.fetchedProgressPortrait(
        action.patientSfId,
        action.abaCaseId,
        action.progressPortraitData,
      );
    } else {
      state.errorFetchProgressPortrait(
        action.patientSfId,
        action.abaCaseId,
        action.errorMessage,
      );
    }
  }
  return state.newState();
};

const useFetchCareEpisode = (
  store: ProgressPortraitStore,
  dispatch: React.Dispatch<ProgressPortraitAction>,
) => {
  useEffect(() => {
    const patientSfId = store.selectedPatientSfId;
    const fetchStatus = store.getCareEpisodes(patientSfId);
    if (fetchStatus === 'not-load') {
      dispatch({
        type: 'FETCHING_CARE_EPISODE',
        patientSfId: patientSfId,
      });
      fetchCareEpisodes(patientSfId)
        .then((careEpisodes) =>
          dispatch({
            type: 'FETCHED_CARE_EPISODE',
            careEpisodes: careEpisodes,
            errorMessage: '',
            patientSfId: patientSfId,
          }),
        )
        .catch((error) =>
          dispatch({
            type: 'FETCHED_CARE_EPISODE',
            careEpisodes: [],
            errorMessage: error,
            patientSfId: patientSfId,
          }),
        );
    }
  }, [store, dispatch]);
};

const useFetchProgressPortraitData = (
  store: ProgressPortraitStore,
  dispatch: React.Dispatch<ProgressPortraitAction>,
) => {
  useEffect(() => {
    const patientSfId = store.selectedPatientSfId;
    const abaCaseSfId = store.getSelectedAbaCaseSfId(patientSfId);
    if (!abaCaseSfId) {
      return;
    }
    const fetchStatus = store.getProgressPortraitData(patientSfId, abaCaseSfId);
    if (fetchStatus === 'not-load') {
      dispatch({
        type: 'FETCHING_PROGRESS_PORTRAIT_DATA',
        patientSfId: patientSfId,
        abaCaseId: abaCaseSfId,
      });
      fetchProgressPortraitMetrics(patientSfId, abaCaseSfId)
        .then((result) => {
          dispatch({
            type: 'FETCHED_PROGRESS_PORTRAIT_DATA',
            progressPortraitData: result,
            patientSfId: patientSfId,
            abaCaseId: abaCaseSfId,
            errorMessage: '',
          });
        })
        .catch((error) => {
          dispatch({
            type: 'FETCHED_PROGRESS_PORTRAIT_DATA',
            progressPortraitData: null,
            patientSfId: patientSfId,
            abaCaseId: abaCaseSfId,
            errorMessage: error,
          });
        });
    }
  }, [dispatch, store]);
};
